"use client";

import { VehicleModelGetDTO } from "@alba-cars/common-modules";
import { useQueries } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import { getAllColors } from "@/lib/api/colors/get-all-colors";
import { getVehicleMakesOptions } from "@/lib/api/get-makes";
import { getVehicleModelsOptions } from "@/lib/api/get-models";

// Types
type Option = {
  value: string;
  label: string;
};

export const useUAECarFormData = (initialMake?: string, options?: { language?: "en" | "ar" | "ru" | "zh" }) => {
  // States for form dependencies
  const [selectedMake, setSelectedMake] = useState<string>(initialMake ?? "");
  const t = useTranslations("Common.forms");

  // Use React Query for API calls
  const queries = useQueries({
    queries: [
      {
        queryKey: ["vehicle-makes"],
        queryFn: () => getVehicleMakesOptions(),
      },
      {
        queryKey: ["vehicle-models", selectedMake],
        queryFn: () => {
          const dto: Omit<VehicleModelGetDTO, "validate"> = {
            filter: {
              makes: [selectedMake],
              validate(): string[] {
                return []; // Implement proper validation if needed
              },
            },
          };
          return getVehicleModelsOptions(dto);
        },
        enabled: !!selectedMake, // Only fetch models when make is selected
      },
      {
        queryKey: ["colors", options?.language],
        queryFn: async () => {
          const colors = await getAllColors(options?.language);

          return colors.map((color) => {
            let localizedName = color.name;

            if (options?.language === "ar") {
              localizedName = color.nameArabic || color.name;
            } else if (options?.language === "ru") {
              localizedName = color.nameRussian || color.name;
            } else if (options?.language === "zh") {
              localizedName = color.nameChinese || color.name;
            }

            return {
              value: color.name,
              label: localizedName,
            };
          });
        },
      },
    ],
  });

  const [
    { data: makesOptions, isLoading: isMakesLoading },
    { data: modelsOptions, isLoading: isModelsLoading },
    { data: colorsOptions, isLoading: isColorsLoading },
  ] = queries;

  // UAE Emirates options
  const emiratesOptions = useMemo<Option[]>(
    () => [
      { value: "dubai", label: "Dubai" },
      { value: "abu_dhabi", label: "Abu Dhabi" },
      { value: "sharjah", label: "Sharjah" },
      { value: "ajman", label: "Ajman" },
      { value: "ras_al_khaimah", label: "Ras Al Khaimah" },
      { value: "fujairah", label: "Fujairah" },
      { value: "umm_al_quwain", label: "Umm Al Quwain" },
    ],
    [],
  );

  const engineTypesOptions = useMemo<Option[]>(
    () => [
      { value: "Electric", label: t("engineTypes.Electric") },
      { value: "3-Cylinder", label: t("engineTypes.3-Cylinder") },
      { value: "4-Cylinder", label: t("engineTypes.4-Cylinder") },
      { value: "5-Cylinder", label: t("engineTypes.5-Cylinder") },
      { value: "V6", label: t("engineTypes.V6") },
      { value: "V8", label: t("engineTypes.V8") },
      { value: "V10", label: t("engineTypes.V10") },
      { value: "W12", label: t("engineTypes.W12") },
    ],
    [t],
  );

  // Mileage ranges
  const mileageOptions = useMemo<Option[]>(
    () => [
      { value: "0-50000", label: "0 - 50,000 km" },
      { value: "50000-100000", label: "50,000 - 100,000 km" },
      { value: "100000-150000", label: "100,000 - 150,000 km" },
      { value: "150000-200000", label: "150,000 - 200,000 km" },
      { value: "200000+", label: "200,000+ km" },
    ],
    [],
  );

  // Vehicle specifications common in UAE
  const specificationsOptions = useMemo<Option[]>(
    () => [
      { value: "gcc", label: "GCC Specs" },
      { value: "imported", label: "Imported" },
      { value: "american", label: "American Specs" },
      { value: "european", label: "European Specs" },
    ],
    [],
  );

  // Vehicle options
  const vehicleOptions = useMemo<Option[]>(
    () => [
      { value: "used", label: "Used" },
      { value: "new", label: "New" },
      { value: "certified_pre_owned", label: "Certified Pre-Owned" },
    ],
    [],
  );

  // Years range (typically 20 years back from current year)
  const yearOptions = useMemo<Option[]>(() => {
    // Check if the current month is November or after
    const currentMonth = new Date().getMonth();
    let currentYear = new Date().getFullYear();

    if (currentMonth >= 10) {
      currentYear += 1;
    }

    const years: Option[] = [];
    for (let year = currentYear; year >= currentYear - 15; year--) {
      years.push({ value: year.toString(), label: year.toString() });
    }

    // Add a separator option
    years.push({ value: "other", label: "Other" });
    return years;
  }, []);

  // Create lookup maps for efficient access
  const makesLookup = useMemo(() => {
    return new Map((makesOptions ?? []).map((make) => [make.value, make]));
  }, [makesOptions]);

  const modelsLookup = useMemo(() => {
    return new Map((modelsOptions ?? []).map((model) => [model.value, model]));
  }, [modelsOptions]);

  const emiratesLookup = useMemo(() => {
    return new Map(emiratesOptions.map((emirate) => [emirate.value, emirate]));
  }, [emiratesOptions]);

  return {
    // Options arrays
    emiratesOptions,
    makesOptions: makesOptions ?? [],
    modelsOptions: modelsOptions ?? [],
    colorsOptions: colorsOptions ?? [],
    mileageOptions,
    specificationsOptions,
    vehicleOptions,
    yearOptions,
    engineTypesOptions,
    // Lookup maps
    makesLookup,
    modelsLookup,
    emiratesLookup,

    // Loading states
    isMakesLoading,
    isModelsLoading,
    isColorsLoading,
    // State setters
    selectedMake,
    setSelectedMake,
  } as const;
};

export type UAECarFormData = ReturnType<typeof useUAECarFormData>;
